import {
  type AccessoryFaceID,
  PergolaDesign,
  type PricingConfiguration,
  type PricingHeightAdjustmentVariant,
  type PricingMatrix,
} from "@core";
import {
  getPrestigeExternalDepthCm,
  getPrestigeExternalHeightCm,
  getPrestigeExternalWidthCm,
} from "./prestigeDimensionDisplay";

// ~3.55 inches — equivalent to the legacy 0.09m epsilon
const AXIS_MATCH_EPSILON_INCHES = 3.55;


/**
 * Post count for the pergola structure depends on design + room count:
 *   wall-mounted + 1 room  →  2 posts  (free edge only)
 *   wall-mounted + 2 rooms →  3 posts  (free edge + mid post)
 *   free-standing + 1 room →  4 posts  (corners)
 *   free-standing + 2 rooms →  6 posts (3 front + 3 back)
 */
const getStructureHeightAdjustmentVariantKey = (payload: {
  design: PergolaDesign;
  widthValue: number;
}): string => {
  const isMultiRoom = payload.widthValue >= 39;
  const wallMounted = payload.design === PergolaDesign.ATTACHED;
  if (isMultiRoom) return wallMounted ? "3_post" : "6_post";
  return wallMounted ? "2_post" : "4_post";
};

// Pricing-config keys for non-placeable add-ons (no faceId / partType wiring).
const LOUVER_LIGHTING_PRODUCT_KEY = "louver_lighting";
const HEATER_FLAT_PRODUCT_KEY = "heater";

const PART_TYPE = {
  MOTORIZED_WIND_SCREEN: 2,
  TILTING_HORIZONTAL_SCREEN_PANEL: 6,
  SLIMLINE_FRAME_GLASS_DOOR: 9,
  WPC_WALL: 12,
  SLIDING_ADJUSTABLE_SHUTTER_PANEL: 17,
} as const;

const ACCESSORY_PRODUCT_KEY_BY_PART_TYPE: Partial<Record<number, string>> = {
  [PART_TYPE.MOTORIZED_WIND_SCREEN]: "wind_screen_150",
  [PART_TYPE.SLIMLINE_FRAME_GLASS_DOOR]: "glass_door_frame_sliding",
  [PART_TYPE.WPC_WALL]: "wpc_wall",
  [PART_TYPE.SLIDING_ADJUSTABLE_SHUTTER_PANEL]: "sliding_adjustable_shutter_panel",
};

const NON_PRICED_ACCESSORY_PART_TYPES = new Set<number>();

export type PricingIssue = {
  code:
    | "missing-structure-price"
    | "missing-accessory-price"
    | "unsupported-accessory"
    | "missing-accessory-product";
  message: string;
  faceId?: AccessoryFaceID;
  partType?: number;
  productKey?: string;
};

export type PricingLineItem = {
  key: string;
  label: string;
  quantity: number;
  unitCost: number;
  unitPrice: number;
  totalCost: number;
  totalPrice: number;
};

export type PrestigePricingResult = {
  accessoryItems: PricingLineItem[];
  accessoryTotalCost: number | null;
  accessoryTotalPrice: number | null;
  issues: PricingIssue[];
  status: "ready" | "incomplete";
  structureItem: PricingLineItem | null;
  structureCost: number | null;
  structurePrice: number | null;
  totalCost: number | null;
  totalPrice: number | null;
};

type SelectedAccessory = {
  faceId: AccessoryFaceID;
  partType: number;
};

export const calculatePrestigePricing = (payload: {
  accessories: SelectedAccessory[];
  design: PergolaDesign;
  depthValue: number;
  heightValue: number;
  pricingConfiguration: PricingConfiguration;
  widthValue: number;
  /** Louver-lighting kit toggle. When true, the LED kit gets priced in. */
  ledLight?: boolean;
  /** Number of 3kW Heater & Bracket units to add to the order. */
  heaterQuantity?: number;
}): PrestigePricingResult => {
  const {
    accessories,
    design,
    depthValue,
    heightValue,
    pricingConfiguration,
    widthValue,
    ledLight = false,
    heaterQuantity = 0,
  } = payload;

  const issues: PricingIssue[] = [];
  const structureMatrix =
    pricingConfiguration.structure.variants[
      design === PergolaDesign.ATTACHED ? "wall_mounted" : "free_standing"
    ];

  const structureResult = structureMatrix
    ? lookupMatrixValues({
        rowInches: cmToInches(getPrestigeExternalDepthCm(depthValue)),
        colInches: cmToInches(getPrestigeExternalWidthCm(widthValue)),
        matrix: structureMatrix,
      })
    : { cost: null, price: null };

  if (structureResult.price === null) {
    issues.push({
      code: "missing-structure-price",
      message: "The current pergola dimensions do not map to a structure price.",
      productKey: pricingConfiguration.structure.key,
    });
  }

  // The structure's height adjustment depends on its actual post count:
  // wall-mounted single = 2, wall-mounted two-room = 3, free-standing single
  // = 4, free-standing two-room = 6. Accessories always use the 2-post column.
  const heightInches = cmToInches(getPrestigeExternalHeightCm(heightValue));
  const structureHeightVariantKey = getStructureHeightAdjustmentVariantKey({
    design,
    widthValue,
  });
  const structureHeightVariant =
    pricingConfiguration.heightAdjustments?.variants[structureHeightVariantKey];
  const structureHeightAdjustment = lookupHeightAdjustment({
    heightInches,
    variant: structureHeightVariant,
  });
  const structureCostWithHeight =
    structureResult.cost !== null
      ? structureResult.cost + (structureHeightAdjustment?.cost ?? 0)
      : null;
  const structurePriceWithHeight =
    structureResult.price !== null
      ? structureResult.price + (structureHeightAdjustment?.price ?? 0)
      : null;

  const accessoryAccumulator = new Map<
    string,
    { key: string; label: string; quantity: number; unitCost: number; unitPrice: number }
  >();

  accessories.forEach((accessory) => {
    if (NON_PRICED_ACCESSORY_PART_TYPES.has(accessory.partType)) {
      return;
    }

    const productKey = ACCESSORY_PRODUCT_KEY_BY_PART_TYPE[accessory.partType];

    if (!productKey) {
      issues.push({
        code: "unsupported-accessory",
        message: `Accessory part type ${accessory.partType} does not have pricing mapped yet.`,
        faceId: accessory.faceId,
        partType: accessory.partType,
      });
      return;
    }

    const product = pricingConfiguration.accessories[productKey];
    const matrix = product?.variants.default;

    if (!matrix) {
      issues.push({
        code: "missing-accessory-product",
        message: `Accessory pricing matrix "${productKey}" is missing from the pricing configuration.`,
        faceId: accessory.faceId,
        partType: accessory.partType,
        productKey,
      });
      return;
    }

    const accessoryResult = lookupMatrixValues({
      rowInches: cmToInches(getPrestigeExternalHeightCm(heightValue)),
      colInches: getAccessoryWidthInches({
        depthValue,
        faceId: accessory.faceId,
        widthValue,
      }),
      matrix,
    });

    if (accessoryResult.price === null) {
      issues.push({
        code: "missing-accessory-price",
        message: `Accessory "${product.label}" does not have a matching matrix price for face ${accessory.faceId}.`,
        faceId: accessory.faceId,
        partType: accessory.partType,
        productKey,
      });
      return;
    }

    // Accessory matrices are keyed by Height × Width, so the matrix value
    // already accounts for height — no separate height-adjustment upcharge.
    const accessoryUnitCost = accessoryResult.cost ?? 0;
    const accessoryUnitPrice = accessoryResult.price;

    const accumulatorKey = `${productKey}:${accessoryUnitCost}:${accessoryUnitPrice}`;
    const existing = accessoryAccumulator.get(accumulatorKey);

    if (existing) {
      existing.quantity += 1;
      return;
    }

    accessoryAccumulator.set(accumulatorKey, {
      key: productKey,
      label: product.label,
      quantity: 1,
      unitCost: accessoryUnitCost,
      unitPrice: accessoryUnitPrice,
    });
  });

  // Louver Lighting (LED kit) — single matrix lookup by (depth × width).
  // The matrix already accounts for the number of LEDs the kit ships with at
  // each size; we just add one unit when the toggle is on.
  if (ledLight) {
    const louverLightingProduct =
      pricingConfiguration.accessories[LOUVER_LIGHTING_PRODUCT_KEY];
    const louverLightingMatrix = louverLightingProduct?.variants.default;

    if (!louverLightingMatrix) {
      issues.push({
        code: "missing-accessory-product",
        message: `Louver lighting pricing matrix is missing from the pricing configuration.`,
        productKey: LOUVER_LIGHTING_PRODUCT_KEY,
      });
    } else {
      const lookup = lookupMatrixValues({
        rowInches: cmToInches(getPrestigeExternalDepthCm(depthValue)),
        colInches: cmToInches(getPrestigeExternalWidthCm(widthValue)),
        matrix: louverLightingMatrix,
      });

      if (lookup.price === null) {
        issues.push({
          code: "missing-accessory-price",
          message: `Louver lighting does not have a matching matrix price for the current dimensions.`,
          productKey: LOUVER_LIGHTING_PRODUCT_KEY,
        });
      } else {
        accessoryAccumulator.set(LOUVER_LIGHTING_PRODUCT_KEY, {
          key: LOUVER_LIGHTING_PRODUCT_KEY,
          label: louverLightingProduct.label,
          quantity: 1,
          unitCost: lookup.cost ?? 0,
          unitPrice: lookup.price,
        });
      }
    }
  }

  // Heater — flat-priced product, multiplied by `heaterQuantity`.
  if (heaterQuantity > 0) {
    const heaterFlat =
      pricingConfiguration.flatProducts?.[HEATER_FLAT_PRODUCT_KEY];

    if (!heaterFlat) {
      issues.push({
        code: "missing-accessory-product",
        message: `Heater pricing is missing from the pricing configuration.`,
        productKey: HEATER_FLAT_PRODUCT_KEY,
      });
    } else {
      accessoryAccumulator.set(HEATER_FLAT_PRODUCT_KEY, {
        key: HEATER_FLAT_PRODUCT_KEY,
        label: heaterFlat.label,
        quantity: heaterQuantity,
        unitCost: heaterFlat.unitCost,
        unitPrice: heaterFlat.unitPrice,
      });
    }
  }

  const accessoryItems = Array.from(accessoryAccumulator.values())
    .map((item) => ({
      ...item,
      totalCost: item.unitCost * item.quantity,
      totalPrice: item.unitPrice * item.quantity,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const accessoryTotalCost =
    issues.length > 0
      ? null
      : accessoryItems.reduce((total, item) => total + item.totalCost, 0);

  const accessoryTotalPrice =
    issues.length > 0
      ? null
      : accessoryItems.reduce((total, item) => total + item.totalPrice, 0);

  const totalCost =
    structureCostWithHeight !== null && accessoryTotalCost !== null
      ? structureCostWithHeight + accessoryTotalCost
      : null;

  const totalPrice =
    structurePriceWithHeight !== null && accessoryTotalPrice !== null
      ? structurePriceWithHeight + accessoryTotalPrice
      : null;

  return {
    accessoryItems,
    accessoryTotalCost,
    accessoryTotalPrice,
    issues,
    status: totalPrice === null ? "incomplete" : "ready",
    structureItem:
      structurePriceWithHeight === null
        ? null
        : {
            key: pricingConfiguration.structure.key,
            label: pricingConfiguration.structure.label,
            quantity: 1,
            unitCost: structureCostWithHeight ?? 0,
            unitPrice: structurePriceWithHeight,
            totalCost: structureCostWithHeight ?? 0,
            totalPrice: structurePriceWithHeight,
          },
    structureCost: structureCostWithHeight,
    structurePrice: structurePriceWithHeight,
    totalCost,
    totalPrice,
  };
};

const getAccessoryWidthInches = (payload: {
  depthValue: number;
  faceId: AccessoryFaceID;
  widthValue: number;
}): number => {
  const { depthValue, faceId, widthValue } = payload;

  if (faceId === "East" || faceId === "West") {
    return cmToInches(getPrestigeExternalDepthCm(depthValue));
  }

  // South/North screens are priced against the pergola width / number of
  // rooms, matched to the price sheet's Width axis. In 2-room mode the rooms
  // split the total width, room 1 taking the extra inch when it's odd. Mirrors
  // the Shopify order path's getAccessoryFaceWidthInches.
  const totalWidthInches = cmToInches(getPrestigeExternalWidthCm(widthValue));
  if (widthValue < 39) {
    return totalWidthInches;
  }
  const room1WidthInches = Math.ceil(totalWidthInches / 2);
  const room2WidthInches = totalWidthInches - room1WidthInches;
  const isRoom2 = faceId === "South1" || faceId === "North1";
  return isRoom2 ? room2WidthInches : room1WidthInches;
};

const lookupMatrixValues = (payload: {
  rowInches: number;
  colInches: number;
  matrix: PricingMatrix;
}): { cost: number | null; price: number | null } => {
  const { rowInches, colInches, matrix } = payload;

  const rowIndex = findNearestAxisIndex({
    axis: matrix.rowAxisInches,
    target: rowInches,
  });
  const colIndex = findNearestAxisIndex({
    axis: matrix.colAxisInches,
    target: colInches,
  });

  if (rowIndex < 0 || colIndex < 0) {
    return { cost: null, price: null };
  }

  const cost = matrix.costRows[rowIndex]?.values[colIndex] ?? null;
  const price = matrix.priceRows[rowIndex]?.values[colIndex] ?? null;

  return {
    cost: typeof cost === "number" ? cost : null,
    price: typeof price === "number" ? price : null,
  };
};

const lookupHeightAdjustment = (payload: {
  heightInches: number;
  variant: PricingHeightAdjustmentVariant | undefined;
}): { cost: number; price: number } | null => {
  const { heightInches, variant } = payload;
  if (!variant) return null;

  const index = findNearestAxisIndex({
    axis: variant.rowAxisInches,
    target: heightInches,
  });
  if (index < 0) return null;

  const cost = variant.costIncrements[index];
  const price = variant.priceIncrements[index];
  if (typeof cost !== "number" || typeof price !== "number") return null;
  return { cost, price };
};

const findNearestAxisIndex = (payload: {
  axis: readonly number[];
  target: number;
}): number => {
  const { axis, target } = payload;

  let nearestIndex = -1;
  let minimumDelta = Number.POSITIVE_INFINITY;

  axis.forEach((value, index) => {
    const delta = Math.abs(value - target);
    if (delta < minimumDelta) {
      minimumDelta = delta;
      nearestIndex = index;
    }
  });

  if (minimumDelta > AXIS_MATCH_EPSILON_INCHES) {
    return -1;
  }

  return nearestIndex;
};

/** Convert centimeters to inches for matrix lookup. */
const cmToInches = (cm: number): number =>
  Math.round((cm / 2.54) * 10000) / 10000;
